import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { SchedulingLink } from "@clockwise/web-commons/src/ui/scheduling-link";
import { SchedulingLinkLoading } from "@clockwise/web-commons/src/ui/scheduling-link-loading";
import { getEditSchedulingLinkPath } from "@clockwise/web-commons/src/util/scheduling.util";
import { getValue } from "@clockwise/web-commons/src/util/errorable.util";
import { RouteRenderer } from "@components/route-renderer";
import { paths } from "@constants/site.constant";
import { getTimeZoneGuess } from "@src/util/time-zone.util";
import { useRouter } from "found";
import { DateTime } from "luxon";
import React, { useMemo, useState } from "react";
import Helmet from "react-helmet";
import BannerVariants from "./banner-variants";
import {
  BookingDetailsQueryDocument,
  RescheduleBookingDocument,
} from "./__generated__/RescheduleBookingRenderer.generated";
import { createUUID } from "@clockwise/client-commons/src/util/uuid";
import {
  BookedSlot,
  DateString,
} from "@clockwise/web-commons/src/ui/scheduling-link/scheduling-link";
import { chunk, compact, head, last } from "lodash";
import { track } from "@clockwise/web-commons/src/util/analytics.util";
import { getDateRange } from "@clockwise/web-commons/src/util/date.util";
import { SlotTimeRange } from "@clockwise/web-commons/src/ui/booking-modal/booking-modal";
import { getFullName } from "@clockwise/web-commons/src/util/profile.util";

export function RescheduleBookingRenderer() {
  const {
    router,
    match: {
      params: { bookingId },
    },
  } = useRouter();
  const timeZone = DateTime.local().zoneName;
  const linkUrl = useMemo(() => window.location.href, []);
  const sessionId = useMemo(createUUID, []);

  const [currentDate, setCurrentDate] = useState<DateString | null>(null);
  // Last day of the month that the user is currently viewing. Can be different than currentDate.
  const [monthViewing, setMonthViewing] = useState<DateString>(
    DateTime.now().startOf("month").toISODate(),
  );
  const [monthsFetched, setMonthsFetched] = useState<{ [date: string]: boolean }>({});
  const [emptyMonthsFetched, setEmptyMonthsFetched] = useState(0);
  const [bookingError, setBookingError] = useState<ApolloError | null>(null);
  const [bookedSlot, setBookedSlot] = React.useState<BookedSlot | null>(null);
  const [isInvalidLink, setIsInvalidLink] = useState(false);

  const { data, loading, error, fetchMore } = useQuery(BookingDetailsQueryDocument, {
    fetchPolicy: "cache-and-network", // quick results from cache, true data back from network.
    notifyOnNetworkStatusChange: true, // This is needed to make the loading variable work according to Apollo docs.
    variables: {
      bookingId,
      timeZone,
      startDate: DateTime.now().toISODate(),
      endDate: DateTime.now().toISODate(),
    },
    onError: (error) => {
      if (error.message.includes?.("no such booking")) {
        setIsInvalidLink(true);
      }
    },
  });

  const [rescheduleBooking, { loading: bookingLoading }] = useMutation(RescheduleBookingDocument, {
    onError: (error) => setBookingError(error),
  });

  const existingBooking = useMemo(() => getValue(data?.viewer.schedulingLinkBooking), [data]);
  const currentUserLinkName = getValue(data?.viewer.user?.linkNameErrorable)?.linkName;
  const linkData = useMemo(() => existingBooking?.schedulingLinkDetails, [existingBooking]);

  const trackSession = React.useCallback(
    (event: string, data: Record<string, any>) => {
      track(event, {
        sessionId,
        meetingSettingsId: linkData?.publicMeetingSettings?.id,
        linkUrl,
        userAgent: window?.navigator?.userAgent,
        ...data,
      });
    },
    [sessionId, linkUrl, linkData],
  );

  const onClearBookingError = (date: string) => {
    if (bookingError) {
      // date is the datestring of the time that we wanted to book. It already includes an offset.
      // To avoid new Date() changing the date string to UTC and then back to the local timezone,
      // creating a date object and explicitly setting the year, month, and day.
      const dateTimeToRefetch = DateTime.fromISO(date);
      const dateToRefetch = new Date();
      dateToRefetch.setFullYear(dateTimeToRefetch.year);
      dateToRefetch.setMonth(dateTimeToRefetch.month - 1);
      dateToRefetch.setDate(dateTimeToRefetch.day);
      const dateTime = DateTime.fromJSDate(dateToRefetch);
      // Refetch the date booked to remove the slots no longer available.
      fetchRangeBatched(dateTime, dateTime);
      setBookingError(null);
    }
  };

  const onRescheduleBooking = async (reason: string | null, selectedTime: SlotTimeRange) => {
    await rescheduleBooking({
      variables: {
        bookingId,
        startTime: selectedTime.startTime.dateOrDateTime,
        endTime: selectedTime.endTime.dateOrDateTime,
        reason,
      },
    });

    setBookedSlot({
      bookedDateOrDateTime: selectedTime.startTime.dateOrDateTime,
      bookedTimeZone: selectedTime.startTime.timeZone || "local",
      reason,
    });
    if (linkData) {
      window.history.replaceState("booked", "", `/c/${linkData.linkName}/${linkData.slug}`);
    }
  };

  const fetchRangeBatched = (start: DateTime, end: DateTime) => {
    // Can't fetch more than 2 weeks at a time, so batch time range into one week slivers.
    const allDates = getDateRange(start.toISODate(), end.toISODate());

    chunk(allDates, 8).map((range) => {
      const start = head(range);
      const end = last(range);

      // Fetch each smaller interval.
      void fetchMore({
        variables: {
          bookingId,
          timeZone,
          startDate: start,
          endDate: end,
        },
      });
    });
  };

  // Watch for needing to fetch the next month.
  React.useEffect(() => {
    if (!currentDate) {
      return;
    }

    const startOfNextMonth = DateTime.fromISO(currentDate).plus({ month: 1 }).startOf("month");

    // If we have endOfAvailability, and the start of the next month is past that date, don't fetch the next month.
    const maybeEndOfAvailability =
      linkData && linkData.publicMeetingSettings.endOfAvailability.dateOrDateTime;
    if (maybeEndOfAvailability && startOfNextMonth > DateTime.fromISO(maybeEndOfAvailability)) {
      return;
    }

    // If we haven't fetched the next month, do so.
    if (!monthsFetched[startOfNextMonth.toISODate()]) {
      const endOfNextMonth = startOfNextMonth.endOf("month");

      fetchRangeBatched(startOfNextMonth, endOfNextMonth);
      setMonthsFetched({
        ...monthsFetched,
        ...{ [startOfNextMonth.toISODate()]: true },
      });
    }
  }, [currentDate]);

  // Watch for need to fetch the current month. This is either the currentDate, or the
  // first day of the month that they have toggled to to look for times.
  // This also kicks off the fetch of the current month on page load.
  React.useEffect(() => {
    // Move monthViewing up to the current day if it is behind today.
    const toFetchStartRange = DateTime.max(DateTime.fromISO(monthViewing), DateTime.now());

    const startRangeFormatted = toFetchStartRange.toISODate();
    if (!monthsFetched[startRangeFormatted]) {
      fetchRangeBatched(toFetchStartRange, toFetchStartRange.endOf("month"));
      setMonthsFetched({
        ...monthsFetched,
        ...{ [startRangeFormatted]: true },
      });
    }
  }, [monthViewing]);

  // Find the first available day and set as currentDate. If next batch of data all empty, fetch another month.
  React.useEffect(() => {
    if (!currentDate && linkData) {
      const firstAvailableDate = linkData.suggestedSlotsByDate.find(
        (byDate) => byDate.suggestedSlots.length,
      );
      if (firstAvailableDate) {
        setCurrentDate(firstAvailableDate.date);
      } else {
        // Fetch more times.
        const fetchedMonths = Object.keys(monthsFetched).sort((month1, month2) =>
          month1 > month2 ? -1 : 1,
        ); // Sort fetched months ascending.
        if (fetchedMonths.length && emptyMonthsFetched < 4) {
          // Set the month viewing to next month, to trigger it to get fetched.
          setMonthViewing(
            DateTime.fromISO(fetchedMonths[fetchedMonths.length - 1])
              .plus({ month: 1 })
              .startOf("month")
              .toISODate(),
          );
          setEmptyMonthsFetched(emptyMonthsFetched + 1);
        }
      }
    }
  }, [data]);

  const isOwner = linkData?.linkName === currentUserLinkName;
  const userEmail = data?.viewer.user?.emails?.[0] ?? null;

  const ownerFullName = getFullName(linkData?.ownerProfile);
  const title = linkData
    ? compact([linkData.publicMeetingSettings.name, ownerFullName]).join(" with ")
    : null;

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex" />
        {title && <title>{title}</title>}
      </Helmet>
      <RouteRenderer
        error={!!error && !loading}
        notFound={isInvalidLink}
        loading={false /* handled by SchedulingLinkLoading */}
      >
        {!error && linkData ? (
          <SchedulingLink
            linkData={linkData}
            ownerEmail={isOwner ? userEmail : null}
            isOwner={linkData.linkName === currentUserLinkName}
            slug={linkData.slug}
            timeZone={getTimeZoneGuess(true)}
            days={linkData.suggestedSlotsByDate}
            setCurrentDate={setCurrentDate}
            setMonthViewing={setMonthViewing}
            currentDate={currentDate}
            loading={loading}
            existingBooking={existingBooking}
            onReschedule={onRescheduleBooking}
            bookingError={bookingError && bookingError.toString?.()}
            clearBookingError={onClearBookingError}
            bookingLoading={bookingLoading}
            bookedSlot={!bookingError && !bookingLoading ? bookedSlot : null}
            trackSession={trackSession}
            highlightBestTimes={linkData.publicMeetingSettings.highlightBestTimes}
            endOfAvailability={linkData.publicMeetingSettings.endOfAvailability.dateOrDateTime}
            onEditLink={() => router.push(getEditSchedulingLinkPath(linkData.slug))}
            onBack={() => router.push(paths.schedulingLinks)}
            banner={
              <BannerVariants
                ownerProfile={linkData.ownerProfile}
                isAuthenticatedUser={!!getValue(data?.viewer?.user)?.id}
              />
            }
          />
        ) : (
          <SchedulingLinkLoading />
        )}
      </RouteRenderer>
    </>
  );
}
